import React from 'react';
import ReactDOM from 'react-dom';

import { v4 as uuid } from 'uuid';

import z from 'z';

export interface IPath {
  link: string;
  text: React.ReactNode;
}

export interface IPrintingFameProps {
  name: string;
}

const PrintingFrame: React.FC<IPrintingFameProps> = ({ name }) => {
  const frame = React.useRef<HTMLIFrameElement>(null);

  const afterPrint = React.useCallback(() => {
    if (!!frame.current) {
      frame.current.innerHTML = '';
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('afterPrint', afterPrint);

    return () => {
      window.removeEventListener('afterprint', afterPrint);
    };
  }, []);

  return (
    <iframe
      ref={frame}
      id="PrintFrame"
      name={name}
      style={{
        display: 'none'
      }}
    />
  );
};

export interface IPrintingLinkProps {
  inPlace: boolean;
  path: IPath;
  target: string;
}

export const PrintingLink: React.FC<IPrintingLinkProps> = ({ inPlace, path, target }) => {
  const href = React.useMemo(() => {
    return inPlace ? path.link : `${path.link}?autoClose=true`;
  }, [inPlace, path]);

  return (
    <a href={href} target={target} style={{ textAlign: 'center', width: '100%', float: 'right' }}>
      {path.text}
    </a>
  );
};

export interface IPrintingLinksProps {
  inPlace: boolean;
  name: string;
  paths: IPath[];
}

const PrintingLinks: React.FC<IPrintingLinksProps> = ({ inPlace, name, paths }) => {
  const target = React.useMemo(() => {
    return inPlace ? name : '_blank';
  }, [inPlace, name]);

  const links = paths.map((path, i) => {
    return <PrintingLink key={i} inPlace={inPlace} path={path} target={target} />;
  });

  return <>{links}</>;
};

export interface IPrintAutoProps {
  paths: IPath[];
}

const PrintAuto: React.FC<IPrintAutoProps> = ({ paths }) => {
  const frames = React.useMemo(() => {
    return paths.map((path, i) => {
      return (
        <iframe
          key={i}
          id={`AutoPrintFrame${i}`}
          src={path.link}
          style={{
            display: 'none'
          }}
        />
      );
    });
  }, [paths]);

  return <>{frames}</>;
};

export interface IProps {
  autoPrint: boolean;
  inPlace: boolean;
  paths: IPath[];
  printingSupported?: boolean;

  onClose: () => void;
}

class PrintWarning extends React.Component<IProps> {
  readonly name: string = uuid();

  render() {
    const boxStyle: React.CSSProperties = {
      position: 'fixed',
      top: '0px',
      zIndex: '10000',
      height: '100%',
      width: '100%',
      backgroundColor: 'rgba(0,0,0,0.4)'
    };

    const overlayStyle: React.CSSProperties = {
      width: '50%',
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      opacity: '1',
      padding: '10px',
      background: 'white',
      margin: 'auto',
      marginTop: '30px'
    };

    const internalStyle: React.CSSProperties = {
      color: '#00496D',
      fontSize: '15px',
      backgroundColor: '#d9edf7',
      borderColor: '#bce8f1',
      padding: '15px',
      paddingBottom: '90px',
      border: '1px solid transparent',
      borderRadius: '4px'
    };

    const xStyle: React.CSSProperties = {
      float: 'right',
      width: '30px',
      height: '30px',
      background: '#D0E1EA',
      color: '#00496D',
      cursor: 'pointer'
    };

    const { autoPrint, inPlace, paths, printingSupported, onClose } = this.props;

    let message;
    if (!printingSupported) {
      message = (
        <div>
          <p style={{ textAlign: 'center' }}>
            Sorry, printing is not currently supported in standalone mode.
          </p>
          <p style={{ textAlign: 'center' }}>
            You can still print by opening this page in your browser as normal.
          </p>
        </div>
      );
    } else {
      message = (
        <>
          <div>
            <p style={{ textAlign: 'center' }}>
              {z.format('{TM3} is currently locked whilst you print. ')}
            </p>
            <p style={{ textAlign: 'center' }}>
              It may take us a moment to prepare your document for printing.
            </p>
            {inPlace ? (
              <>
                <p style={{ textAlign: 'center' }}>
                  You can use the following link(s) print your document(s).
                </p>
                <p style={{ textAlign: 'center' }}>
                  If no printing opened for you this may be caused by your browser blocking pop-ups.
                </p>
              </>
            ) : (
              <>
                <p style={{ textAlign: 'center' }}>
                  If no printing opened for you this may be caused by your browser blocking pop-ups.
                </p>
                <p style={{ textAlign: 'center' }}>
                  You can use the following link(s) to open the document to print in a new tab:
                </p>
              </>
            )}
            <PrintingLinks inPlace={inPlace} name={this.name} paths={paths} />
          </div>
          <PrintingFrame name={this.name} />
        </>
      );
    }

    return (
      <>
        <div style={boxStyle}>
          <div style={overlayStyle}>
            <div style={xStyle} onClick={onClose}>
              <span style={{ float: 'right', width: '60%', fontSize: '17px' }}>x</span>
            </div>
            <div style={internalStyle}>{message}</div>
          </div>
        </div>
        {printingSupported && autoPrint && inPlace && <PrintAuto paths={paths} />}
      </>
    );
  }
}

abstract class PrintUtil {
  static print(paths: IPath[]) {
    let printingSupported = true;
    let autoPrinting = true;

    if (window.navigator.standalone) {
      printingSupported = false;
    }

    if (paths.length > 1 && navigator.userAgent.match(/iPad/i)) {
      autoPrinting = false;
    }

    const androidPrinting = /(android)/i.test(navigator.userAgent);

    const warning = document.createElement('div');
    document.body.appendChild(warning);
    ReactDOM.render(
      <PrintWarning
        autoPrint={autoPrinting}
        inPlace={!androidPrinting}
        printingSupported={printingSupported}
        paths={paths}
        onClose={() => {
          ReactDOM.unmountComponentAtNode(warning);
          document.body.removeChild(warning);
        }}
      />,
      warning
    );

    if (androidPrinting) {
      return paths.forEach((link) => {
        const w = window.open('', '_blank');
        w!.location.href = link.link;
      });
    }
  }
}

export default PrintUtil;
